import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { galleryImages as mockGalleryImages } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Gallery = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [galleryImages, setGalleryImages] = useState(mockGalleryImages);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/gallery`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            // Transform API data to match component format
            const apiImages = data.map((item, index) => ({
              id: `api-${item.id || index + 1}`,
              image: item.url,
              title: item.title || `Gallery image ${index + 1}`
            }));
            // Combine: API images first, then original mock images
            setGalleryImages([...apiImages, ...mockGalleryImages]);
          }
          // If API is empty, keep using mockGalleryImages (already set as default)
        }
      } catch (error) {
        console.error('Error fetching gallery:', error);
        // Keep using mockGalleryImages on error
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    const newIndex = selectedIndex === 0 ? galleryImages.length - 1 : selectedIndex - 1;
    setSelectedIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    const newIndex = selectedIndex === galleryImages.length - 1 ? 0 : selectedIndex + 1;
    setSelectedIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious(e);
      if (e.key === 'ArrowRight') goToNext(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, selectedIndex]);
  
  return (
    <div 
      className="py-20 px-4 transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#000000' : '#ffffff' }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 
            className="text-4xl md:text-5xl font-bold mb-4 transition-colors duration-300"
            style={{ 
              color: isDark ? '#ff00ff' : '#ff1493',
              textShadow: isDark ? '0 0 30px rgba(255, 0, 255, 0.8), 0 0 50px rgba(255, 0, 255, 0.5)' : '0 0 25px rgba(255, 20, 147, 0.6), 0 0 40px rgba(255, 20, 147, 0.4)'
            }}
          >
            {t('gallery.title')}
          </h2>
          <p 
            className="text-lg max-w-2xl mx-auto transition-colors duration-300"
            style={{ 
              color: isDark ? '#00ffff' : '#00bfff',
              textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.6)' : '0 0 10px rgba(0, 191, 255, 0.5)'
            }}
          >
            {t('gallery.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 md:gap-4">
          {galleryImages.map((item, index) => (
            <Card 
              key={item.id}
              className="group overflow-hidden transition-all duration-300 hover:scale-105 cursor-pointer"
              style={{ 
                animationDelay: `${index * 100}ms`,
                backgroundColor: isDark ? '#0a0a0a' : '#ffffff',
                borderColor: isDark ? '#ff00ff' : '#9333ea',
                borderWidth: '2px',
                boxShadow: isDark ? '0 0 20px rgba(255, 0, 255, 0.3)' : '0 0 18px rgba(147, 51, 234, 0.35)'
              }}
              onClick={() => openLightbox(item, index)}
            >
              <div className="relative aspect-square overflow-hidden">
                <img 
                  src={item.image}
                  alt={`Neon sign ${item.id}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: isDark 
                      ? 'linear-gradient(to top, #000000 0%, rgba(0, 0, 0, 0.5) 50%, transparent 100%)'
                      : 'linear-gradient(to top, #f8fafc 0%, rgba(248, 250, 252, 0.2) 50%, transparent 100%)'
                  }}
                />
              </div>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p 
            className="mb-6 transition-colors duration-300"
            style={{ 
              color: isDark ? '#ffff00' : '#ff8c00',
              textShadow: isDark ? '0 0 10px rgba(255, 255, 0, 0.6)' : '0 0 10px rgba(255, 140, 0, 0.5)'
            }}
          >
            {t('gallery.customDesign')}
          </p>
          <button 
            className="px-8 py-3 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105"
            data-testid="gallery-get-quote-btn"
            onClick={() => {
              const quoteSection = document.getElementById('quote');
              if (quoteSection) {
                quoteSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            style={{
              background: isDark 
                ? 'linear-gradient(135deg, #00ff00 0%, #00ff88 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
              boxShadow: isDark 
                ? '0 10px 50px rgba(0, 255, 136, 0.6), 0 0 30px rgba(0, 255, 0, 0.4)'
                : '0 10px 40px rgba(16, 185, 129, 0.5), 0 0 25px rgba(20, 184, 166, 0.4)'
            }}
          >
            {t('gallery.getQuote')}
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 p-2 rounded-full transition-all duration-300 hover:scale-110"
            style={{
              backgroundColor: isDark ? 'rgba(255, 0, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff'
            }}
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous button */}
          <button
            className="absolute left-4 p-3 rounded-full transition-all duration-300 hover:scale-110"
            style={{
              backgroundColor: isDark ? 'rgba(255, 0, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff'
            }}
            onClick={goToPrevious}
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          {/* Next button */}
          <button
            className="absolute right-4 p-3 rounded-full transition-all duration-300 hover:scale-110"
            style={{
              backgroundColor: isDark ? 'rgba(255, 0, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff'
            }}
            onClick={goToNext}
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Image */}
          <div 
            className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.image}
              alt={selectedImage.title || `Gallery image ${selectedIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              style={{
                boxShadow: isDark 
                  ? '0 0 60px rgba(255, 0, 255, 0.5)' 
                  : '0 0 40px rgba(255, 255, 255, 0.3)'
              }}
            />
          </div>

          {/* Image counter */}
          <div 
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full"
            style={{
              backgroundColor: isDark ? 'rgba(255, 0, 255, 0.3)' : 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff'
            }}
          >
            {selectedIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;